import { MenuItemInfo } from '@tableau/widgets';
import React from 'react';

import { AddNewUserDropdownForm } from '~src/authentication/components/identityPools/userImport/AddNewUserDropdownForm';
import { IIdentityPool } from '~src/authentication/serverApi/types/identityPools/IIdentityPool';
import { ServerSettingsUnauthenticated } from '~src/settings/services/ServerSettingsUnauthenticated';
import { ISelectFilterDefinitionOption } from '~src/spacesPlatform/services/FilterPanelService';
import { FilterMap } from '~src/spacesPlatform/state/ContentExplorerState';

import { Messages } from '../../codegen/Localize';
import { DomainFamily } from '../../settings/types/IServerSettingsUnauthenticated';
import { ActiveDirectoryGroupAction } from '../groups/services/actions/ActiveDirectoryGroupAction';
import { AddActiveDirectoryUsersAction } from '../services/AddActiveDirectoryUsersAction';
import { AddExternalUsersAction } from '../services/AddExternalUsersAction';
import { AddLocalUserAction, AddLocalUserDialogType } from '../services/AddLocalUserAction';
import { BulkImportUsersAction } from '../services/BulkImportUsersAction';
import { hasSiteLicensingConsumptionInfo } from '../services/licenses/LicensesService';
import { ServerUsers } from '../services/resources/ServerUsers';
import { SiteUsers } from '../services/resources/SiteUsers';
import { AddContentButton } from './AddContentButton';

export interface TriggerAddUsersButtonActionProps {
  readonly isServerUsersSpace: boolean;
  readonly requestSpaceContents?: () => void;
  readonly buyLicense?: () => void;
  readonly updateTotalUserCountForServerUserSpace?: (maxSiteRole?: string, maxSiteRoleCounts?: ISelectFilterDefinitionOption<string>[]) => void;
  readonly mixedContentFilterMap?: FilterMap;
  readonly selectedIdentityPool?: IIdentityPool;
}

export interface AddUsersButtonActionProps extends TriggerAddUsersButtonActionProps {
  readonly addButtonText: string;
  readonly showBuyLicensesOption?: boolean;
  readonly identityStoreType: string;
}

export interface AddUsersButtonProps extends AddUsersButtonActionProps {
  readonly shouldRenderAsForm?: boolean;
  readonly onOptionSelected?: (selection: AddUserDialogType) => void;
  readonly currentSelection?: AddUserDialogType;
}

export enum AddUserDialogType {
  AddLocalUser = 'addLocalUser',
  AddADUser = 'addADUser',
  AddExternalUser = 'addExternalUser',
  BulkImportUsers = 'bulkImportUsers',
  ImportADGroup = 'importADGroup',
  BuyLicenses = 'buyLicenses',
}

const ADD_USERS_TEST_ID = 'action-add-users';
const ADD_USERS_OR_LICENSE_TEST_ID = 'action-add-users-or-licenses';

function getAvailableQuota() {
  return hasSiteLicensingConsumptionInfo().then((hasCapacity: boolean) => {
    if (!hasCapacity) {
      return SiteUsers.instance.getRemainingUserQuota();
    } else {
      return undefined;
    }
  });
}

export async function triggerAddUsersButtonAction(props: TriggerAddUsersButtonActionProps, addUserDialogType: AddUserDialogType): Promise<void> {
  let modalInstancePromise;

  switch(addUserDialogType) {
    case AddUserDialogType.AddLocalUser:
    {
      let addLocalUserDialogType = props.isServerUsersSpace ? AddLocalUserDialogType.SERVER : AddLocalUserDialogType.SITE;
      let availableQuota = props.isServerUsersSpace ? undefined : await getAvailableQuota();
      modalInstancePromise = AddLocalUserAction.instance.showAddLocalUserDialog(addLocalUserDialogType, availableQuota, props.selectedIdentityPool);
      break;
    }
    case AddUserDialogType.AddADUser:
    {
      let availableQuota = props.isServerUsersSpace ? undefined : await getAvailableQuota();
      modalInstancePromise = props.isServerUsersSpace ?
        AddActiveDirectoryUsersAction.showAddActiveDirectoryUsersToServerDialog(props.selectedIdentityPool) :
        AddActiveDirectoryUsersAction.showAddActiveDirectoryUsersToSiteDialog(availableQuota, props.selectedIdentityPool);
      break;
    }
    case AddUserDialogType.AddExternalUser:
    {
      let availableQuota = await getAvailableQuota();
      modalInstancePromise = AddExternalUsersAction.showAddExternalUsersToSiteDialog(availableQuota);
      break;
    }
    case AddUserDialogType.BulkImportUsers:
      modalInstancePromise = props.isServerUsersSpace ?
        BulkImportUsersAction.showBulkImportServerUsersDialog(props.selectedIdentityPool) :
        BulkImportUsersAction.showBulkImportSiteUsersDialog(props.selectedIdentityPool);
      break;
    case AddUserDialogType.ImportADGroup:
      modalInstancePromise = ActiveDirectoryGroupAction.showAddAdGroupDialog();
      break;
    case AddUserDialogType.BuyLicenses:
      props.buyLicense();
      return;
    default:
      return;
  }

  modalInstancePromise.then(async () => {
    props.requestSpaceContents();
    if (props.isServerUsersSpace) {
      const siteRoleOptionsWithCounts = await ServerUsers.instance.getUserCountsByMaxSiteRoleWithoutSolr(props.mixedContentFilterMap);
      props.updateTotalUserCountForServerUserSpace(undefined, siteRoleOptionsWithCounts);
    }
  }).catch(() => {
    // If the user cancels the dialog that's fine, there's no action for us to do, we can swallow the error. 
    // No cleanup is necessary and this is an expected outcome.
  });
}


export class AddUsersButton extends React.Component<AddUsersButtonProps> {

  public constructor(props: AddUsersButtonProps) {
    super(props);
  }

  public createAddServerUserMenuItemOptions = (): MenuItemInfo[] => {
    let menuItemInfos: MenuItemInfo[] = [];

    if (this.props.identityStoreType === DomainFamily.Local) {
      menuItemInfos.push({displayValue: Messages.addUsersLocalButton(), value: AddUserDialogType.AddLocalUser});
    } else if (this.props.identityStoreType === DomainFamily.ActiveDirectory) {
      const displayValue = ServerSettingsUnauthenticated.isLdapStore() ? Messages.addUsersImportLdapButton() : Messages.addUsersImportAdButton();
      menuItemInfos.push({displayValue: displayValue, value: AddUserDialogType.AddADUser});
    }

    menuItemInfos.push({displayValue: Messages.addUsersBulkImportButton(), value: AddUserDialogType.BulkImportUsers});
    return menuItemInfos;
  };

  public createAddSiteUserMenuItemOptions = (): MenuItemInfo[] => {
    let menuItemInfos: MenuItemInfo[] = [];

    if (this.props.identityStoreType === DomainFamily.Local) {
      menuItemInfos.push({displayValue: Messages.addUsersLocalButton(), value: AddUserDialogType.AddLocalUser});
    } else if (this.props.identityStoreType === DomainFamily.ActiveDirectory) {
      const isLdapStore = ServerSettingsUnauthenticated.isLdapStore();
      const importUserDisplayValue = isLdapStore ? Messages.addUsersImportLdapButton() : Messages.addUsersImportAdButton();
      const importGroupDisplayValue = isLdapStore ? Messages.addUsersImportLdapGroupButton() : Messages.addUsersImportAdGroupButton();
      menuItemInfos.push({displayValue: importUserDisplayValue, value: AddUserDialogType.AddADUser});
      menuItemInfos.push({displayValue: importGroupDisplayValue, value: AddUserDialogType.ImportADGroup});
    } else if (this.props.identityStoreType === DomainFamily.External) {
      menuItemInfos.push({displayValue: Messages.addUsersImportExternalButton(), value: AddUserDialogType.AddExternalUser});
    }

    menuItemInfos.push({displayValue: Messages.addUsersBulkImportButton(), value: AddUserDialogType.BulkImportUsers});

    if (this.props.showBuyLicensesOption) {
      menuItemInfos.push({isDivider: true});
      menuItemInfos.push({displayValue: Messages.buyLicenses(), value: AddUserDialogType.BuyLicenses});
    }

    return menuItemInfos;
  };

  public render(): JSX.Element {
    const menuItems = this.props.isServerUsersSpace ? this.createAddServerUserMenuItemOptions() : this.createAddSiteUserMenuItemOptions();
    if (this.props.shouldRenderAsForm) {
        // Here we will show the same options within a dialog-form experience, rendered after the user selected an identity pool.
        return <AddNewUserDropdownForm menuItems={menuItems} {...this.props}/>;
    } else {
      // Note: This button is the experience when no new identity pools are configured in that server, a button in the Users list page.
      //       Whith IdPools, the strategy will render an alternative button, that will open the "Select pool"
      //       dialog, which will then eventually delegate on the same addUsers actions we see here, through a dialog instead of buttons.
      return <AddContentButton
          buttonText={this.props.addButtonText}
          testId={this.props.showBuyLicensesOption ? ADD_USERS_OR_LICENSE_TEST_ID : ADD_USERS_TEST_ID}
          menuItems={menuItems}
          onSelect={(selection: AddUserDialogType) => triggerAddUsersButtonAction(this.props, selection)}
        />;
    }
  }
}

